'use client';

import { getAssetUrl } from '@/lib/contentful/utils';
import clsx from 'clsx';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './CategoryBannerGrid.module.scss';
import type { CategoryBannerGridProps } from './CategoryBannerGrid.types';
import { ASPECT_RATIO_MAP, SPACING_MAP } from './CategoryBannerGrid.utils';
import { CategoryBannerItem } from './components/CategoryBannerItem';

import { CMSCategoryBannerGrid } from '@/lib/cms/types';
import { CategoryBannerGridFragment, SpacingSize } from '@/lib/contentful/types';
import 'swiper/css';
import 'swiper/css/free-mode';

export type { CategoryBannerGridProps };

const MOBILE_SPACE_BETWEEN = 4;
const SWIPER_TOUCH_THRESHOLD = 8;

const SWIPER_FREE_MODE = { enabled: true, sticky: false } as const;

const getSpacing = (value: SpacingSize): string =>
  value in SPACING_MAP ? SPACING_MAP[value] : SPACING_MAP.none;

export const CategoryBannerGrid = ({ data, className, cmsIndex }: CategoryBannerGridProps) => {
  const rawData = data as CategoryBannerGridFragment;
  const cmsData = data as CMSCategoryBannerGrid;
  const banners = (rawData.bannersCollection?.items || cmsData.items || []).filter(Boolean);

  const testIdPrefix = cmsIndex !== undefined ? `categorybannergrid-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  const swiperBreakpoints = useMemo(
    () => ({
      768: {
        slidesPerView: banners.length,
        spaceBetween: MOBILE_SPACE_BETWEEN,
        freeMode: { enabled: false },
        allowTouchMove: false,
        simulateTouch: false,
      },
    }),
    [banners.length]
  );

  if (banners.length === 0) {
    return null;
  }

  const marginTop = (data.marginTop || 'none') as SpacingSize;
  const marginBottom = (data.marginBottom || 'none') as SpacingSize;
  const componentPadding = (data.componentPadding || 'none') as SpacingSize;
  const aspectRatio = data.bannerAspectRatio
    ? ASPECT_RATIO_MAP[data.bannerAspectRatio as keyof typeof ASPECT_RATIO_MAP] ||
      ASPECT_RATIO_MAP.portrait
    : ASPECT_RATIO_MAP.portrait;
  const gridTransition = (data.transitionType || 'none') as 'none' | 'fade' | 'stagger';

  const backgroundImageUrl =
    cmsData.backgroundImage?.url || getAssetUrl(rawData.backgroundImage) || undefined;

  const sectionStyle: CSSProperties = {
    marginTop: getSpacing(marginTop),
    marginBottom: getSpacing(marginBottom),
  };

  const wrapperStyle: CSSProperties =
    componentPadding !== 'none'
      ? {
          padding: getSpacing(componentPadding),
        }
      : {};

  return (
    <div
      className={clsx(styles.wrapper, componentPadding !== 'none' && styles.hasPadding)}
      style={wrapperStyle}
    >
      <section
        className={clsx(styles.section, className)}
        style={sectionStyle}
        data-testid={testId('section')}
      >
        {backgroundImageUrl && (
          <div className={styles.backgroundImageWrapper}>
            <Image
              src={backgroundImageUrl}
              alt=""
              fill
              className={styles.backgroundImage}
              sizes="100vw"
            />
          </div>
        )}

        <Swiper
          className={styles.grid}
          modules={[FreeMode]}
          slidesPerView="auto"
          spaceBetween={MOBILE_SPACE_BETWEEN}
          freeMode={SWIPER_FREE_MODE}
          threshold={SWIPER_TOUCH_THRESHOLD}
          touchStartPreventDefault={false}
          breakpoints={swiperBreakpoints}
          data-testid={testId('grid')}
        >
          {banners.map((banner, index) => (
            <SwiperSlide key={banner.sys?.id || index} className={styles.slide}>
              <CategoryBannerItem
                banner={banner}
                index={index}
                gridTransition={gridTransition}
                aspectRatio={aspectRatio}
                testIdPrefix={testIdPrefix}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>
    </div>
  );
};
